import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { FileText, Download, Plus, Trash2, Calendar, TrendingUp, Euro, Wallet } from "lucide-react";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MONATE = [
  { value: 1, label: "Januar" }, { value: 2, label: "Februar" },
  { value: 3, label: "März" }, { value: 4, label: "April" },
  { value: 5, label: "Mai" }, { value: 6, label: "Juni" },
  { value: 7, label: "Juli" }, { value: 8, label: "August" },
  { value: 9, label: "September" }, { value: 10, label: "Oktober" },
  { value: 11, label: "November" }, { value: 12, label: "Dezember" },
];

const formatCurrency = (value) =>
  new Intl.NumberFormat("de-DE", { style: "currency", currency: "EUR" }).format(value || 0);

export const Monatsberichte = () => {
  const now = new Date();
  const defaultMonat = now.getMonth() === 0 ? 12 : now.getMonth();
  const defaultJahr = now.getMonth() === 0 ? now.getFullYear() - 1 : now.getFullYear();

  const [berichte, setBerichte] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [monat, setMonat] = useState(defaultMonat);
  const [jahr, setJahr] = useState(defaultJahr);

  useEffect(() => {
    fetchBerichte();
  }, []);

  const fetchBerichte = async () => {
    try {
      const res = await axios.get(`${API}/monatsberichte`, { withCredentials: true });
      setBerichte(res.data);
    } catch (e) {
      toast.error("Fehler beim Laden der Monatsberichte");
    } finally {
      setLoading(false);
    }
  };

  const handleGenerieren = async () => {
    setGenerating(true);
    try {
      await axios.post(
        `${API}/monatsberichte/generieren?monat=${monat}&jahr=${jahr}`,
        {},
        { withCredentials: true }
      );
      toast.success(`Bericht ${MONATE[monat - 1].label} ${jahr} generiert`);
      fetchBerichte();
    } catch (e) {
      toast.error("Fehler beim Generieren");
    } finally {
      setGenerating(false);
    }
  };

  const handleDownload = async (id, monatName, j) => {
    try {
      const res = await axios.get(`${API}/monatsberichte/${id}/pdf`, {
        withCredentials: true,
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `Monatsbericht_${j}_${monatName}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Fehler beim PDF-Download");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Diesen Bericht wirklich loeschen?")) return;
    try {
      await axios.delete(`${API}/monatsberichte/${id}`, { withCredentials: true });
      toast.success("Bericht geloescht");
      fetchBerichte();
    } catch (e) {
      toast.error("Fehler beim Loeschen");
    }
  };

  const jahre = [defaultJahr, defaultJahr - 1, defaultJahr - 2];

  return (
    <div data-testid="monatsberichte-page">
      <header className="page-header">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="page-title">Monatsberichte</h1>
            <p className="page-subtitle">Automatische BWA-Berichte mit Umsatz, Marge und Personalkosten</p>
          </div>
        </div>
      </header>

      {/* Generieren-Box */}
      <div className="bg-white border border-stone-200 rounded-sm p-6 mb-6">
        <h2 className="font-display text-base font-semibold text-[#3A4D39] mb-4 flex items-center gap-2">
          <Plus size={18} /> Neuen Bericht erstellen
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 items-end">
          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-wide text-[#5C5C5C]">Monat</Label>
            <Select value={monat.toString()} onValueChange={(v) => setMonat(parseInt(v))}>
              <SelectTrigger className="border-stone-300 rounded-sm" data-testid="select-monat">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {MONATE.map((m) => (
                  <SelectItem key={m.value} value={m.value.toString()}>{m.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-wide text-[#5C5C5C]">Jahr</Label>
            <Select value={jahr.toString()} onValueChange={(v) => setJahr(parseInt(v))}>
              <SelectTrigger className="border-stone-300 rounded-sm" data-testid="select-jahr">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {jahre.map((j) => (
                  <SelectItem key={j} value={j.toString()}>{j}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <Button
            onClick={handleGenerieren}
            disabled={generating}
            className="bg-[#3A4D39] hover:bg-[#2C3B2B] text-white rounded-sm"
            data-testid="btn-generate-monatsbericht"
          >
            <FileText size={16} className="mr-2" />
            {generating ? "Generiere..." : "Bericht generieren"}
          </Button>
        </div>
        <p className="text-xs text-slate-500 mt-3">
          Aggregiert Umsaetze, Wareneinsatz, Personal- und Fixkosten zum gewaehlten Monat. Bestehende Berichte zum gleichen Zeitraum werden ueberschrieben.
        </p>
      </div>

      {/* Liste */}
      {loading ? (
        <div className="text-center text-slate-500 py-8">Laden...</div>
      ) : berichte.length === 0 ? (
        <div className="text-center text-slate-500 py-12 bg-white border border-stone-200 rounded-sm" data-testid="empty-monatsberichte">
          <FileText size={32} className="mx-auto mb-3 text-slate-300" />
          <p>Noch keine Monatsberichte erstellt.</p>
          <p className="text-xs mt-1">Erstelle oben deinen ersten Bericht.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="monatsberichte-list">
          {berichte.map((b) => (
            <div
              key={b.id}
              className="bg-white border border-stone-200 rounded-sm p-5 hover:shadow-sm transition-shadow"
              data-testid={`bericht-card-${b.id}`}
            >
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-2">
                  <Calendar size={16} className="text-[#3A4D39]" />
                  <h3 className="font-display text-base font-semibold text-[#3A4D39]">
                    {b.monat_name} {b.jahr}
                  </h3>
                  <span className="text-xs bg-stone-100 text-stone-600 px-2 py-0.5 rounded">{b.land}</span>
                </div>
                <span className="text-xs text-slate-400">
                  {new Date(b.erstellt_am).toLocaleDateString("de-DE")}
                </span>
              </div>

              <div className="grid grid-cols-2 gap-3 mb-4">
                <div>
                  <div className="flex items-center gap-1 text-xs text-slate-500">
                    <Euro size={12} /> Umsatz netto
                  </div>
                  <div className="font-mono text-sm font-semibold">{formatCurrency(b.umsatz_netto)}</div>
                </div>
                <div>
                  <div className="flex items-center gap-1 text-xs text-slate-500">
                    <TrendingUp size={12} /> Rohertragsmarge
                  </div>
                  <div className="font-mono text-sm font-semibold">{(b.rohertragsmarge ?? 0).toFixed(1)}%</div>
                </div>
                <div>
                  <div className="flex items-center gap-1 text-xs text-slate-500">
                    <Wallet size={12} /> Personalkosten
                  </div>
                  <div className="font-mono text-sm">{formatCurrency(b.personalkosten)}</div>
                </div>
                <div>
                  <div className="text-xs text-slate-500">Betriebsergebnis</div>
                  <div
                    className={`font-mono text-sm font-semibold ${
                      (b.betriebsergebnis || 0) >= 0 ? "text-emerald-700" : "text-red-700"
                    }`}
                  >
                    {formatCurrency(b.betriebsergebnis)} ({(b.ebit_marge ?? 0).toFixed(1)}%)
                  </div>
                </div>
              </div>

              <div className="flex gap-2 pt-3 border-t border-stone-100">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => handleDownload(b.id, b.monat_name, b.jahr)}
                  className="rounded-sm flex-1"
                  data-testid={`btn-download-${b.id}`}
                >
                  <Download size={14} className="mr-1.5" /> PDF
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => handleDelete(b.id)}
                  className="rounded-sm text-red-600 hover:bg-red-50 hover:text-red-700"
                  data-testid={`btn-delete-${b.id}`}
                >
                  <Trash2 size={14} />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default Monatsberichte;
